import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { CommunityRoleBadge } from '../../models/community.models';

/** CoreUI theme colours a badge may name; anything else is treated as a CSS colour. */
const THEME_COLORS = new Set(['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark']);

/**
 * Role badges next to an author's name (F13). The backend has already filtered
 * them for the channel the content is shown in, so this only renders. Renders
 * nothing without badges, so it can sit next to any name without an `@if`.
 */
@Component({
  selector: 'app-community-badges',
  standalone: true,
  imports: [TranslocoModule],
  template: `
    <ng-container *transloco="let t">
      @for (badge of badges ?? []; track badge.key) {
        <span [class]="badgeClass(badge)"
              [style.background-color]="customColor(badge)"
              [title]="t('community.badges.role', { label: badge.label })">{{ badge.label }}</span>
      }
    </ng-container>
  `,
  styles: [`
    .community-badge { font-size: 0.7em; vertical-align: middle; font-weight: 600; }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunityBadgesComponent {
  @Input() badges: CommunityRoleBadge[] | null | undefined = [];

  badgeClass(badge: CommunityRoleBadge): string {
    const color = (badge.color || 'secondary').toLowerCase();
    if (!THEME_COLORS.has(color)) return 'badge community-badge ms-1 text-white';
    const dark = color === 'warning' || color === 'info' || color === 'light';
    return `badge community-badge ms-1 bg-${color}${dark ? ' text-dark' : ''}`;
  }

  /** Inline colour only for non-theme values (e.g. `#8e44ad`). */
  customColor(badge: CommunityRoleBadge): string | null {
    const color = (badge.color || '').toLowerCase();
    return color && !THEME_COLORS.has(color) ? badge.color : null;
  }
}
